import {
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined'
import { COLORS } from 'app-constants'
import { useEffect, useState } from 'react'
import AddTopicModal from './AddTopicModal'
import { myTwinAtom } from 'state/my-twin'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { useFetchInsightCategories } from 'hooks/use-insights'
import { TwinInsightCategory, TwinInsightCategoryType } from 'api/model'
import { useActiveOrganization } from 'hooks/use-organization'
import {
  twinsDeleteInsightCategories,
  twinsUpdateInsightCategories,
  twinsUpdateInsightCategory,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { toastAtom } from 'state'
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import JelledModal from 'components/JelledModal'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { isFieldEmpty } from 'utils'
import { TextFieldWithDarkMode } from 'dark-mode-components'
import styled from '@emotion/styled'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import TopicLabelPicker from './TopicLabelPicker/TopicLabelPicker'

export const SidebarContainerStack = styled(Stack)`
  height: fit-content;
  padding: 12px;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid #eeeeee;
  background: #ffffff;
`

const EditTopicModal = ({
  twinId,
  insightCategory,
  onClose,
  onTopicEdited,
}: {
  twinId: string
  insightCategory: TwinInsightCategory
  onClose: () => void
  onTopicEdited: (twinInsightCategory: TwinInsightCategory) => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)
  const theme = useTheme()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitCount, setSubmitCount] = useState(0)

  const [name, setName] = useState<string>('')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    setSubmitCount(0)
  }

  const [instruction, setInstruction] = useState<string>('')
  const onInstructionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInstruction(event.target.value)
    setSubmitCount(0)
  }

  useEffect(() => {
    setName(insightCategory.name)
    if (insightCategory.instruction) {
      setInstruction(insightCategory.instruction)
    }
  }, [insightCategory])

  const onEditTopic = async () => {
    setIsSubmitting(true)
    setSubmitCount((prev) => prev + 1)

    if (!activeOrganization || !twinId || isFieldEmpty(name)) {
      return
    }

    const updatedInsight = {
      id: insightCategory.id,
      name,
      instruction: instruction || undefined,
    }

    try {
      const response = await twinsUpdateInsightCategories(activeOrganization, twinId, [updatedInsight])

      // TODO: this is broken at the moment, need to fix
      // update local state with new insight
      if (response.data && response.data.length > 0) {
        onTopicEdited(response.data[0])
      }
      setToast({ title: 'Successfully edited the topic', severity: 'success' })
      onClose()
    } catch (e) {
      console.log('e', e)
      setToast({ title: 'Failed to edit topic', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <JelledModal
      onClose={onClose}
      isOpen={true}
      width={isMobile ? '100%' : '675px'}
      modalStyle={{
        backgroundColor: COLORS.PURE_WHITE,
        marginLeft: isMobile ? '16px' : 0,
        marginRight: isMobile ? '16px' : 0,
      }}>
      <Typography mb={6} variant="h4" color="secondary">
        Edit topic
      </Typography>

      <TextFieldWithDarkMode
        className="has-label"
        isDarkBackdrop={false}
        required
        theme={theme}
        label="Name"
        placeholder="Add a topic name"
        value={name}
        onChange={onNameChange}
        disabled={isSubmitting}
        error={submitCount > 0 && isFieldEmpty(name)}
        helperText={submitCount > 0 && isFieldEmpty(name) && 'You must enter a name'}
        sx={{
          mb: 4,
        }}
      />

      <TextFieldWithDarkMode
        className="has-label"
        isDarkBackdrop={false}
        theme={theme}
        label="Instruction"
        placeholder="Add an instruction"
        value={instruction}
        onChange={onInstructionChange}
        disabled={isSubmitting}
        sx={{
          mb: 4,
        }}
      />

      <Stack direction={isMobile ? 'column' : 'row'} justifyContent="flex-end" spacing={2} mt={4}>
        <Stack direction="row" alignItems="center" spacing={1} alignSelf="flex-end">
          <Button
            onClick={() => onClose()}
            size="medium"
            sx={{
              color: 'primary.main',
            }}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" size="medium" onClick={onEditTopic}>
            Save
          </Button>
        </Stack>
      </Stack>
    </JelledModal>
  )
}

const TopicItemMenu = ({
  twinId,
  color,
  isDeletionDisabled,
  onOpenEditTopicModal,
  onDeleteTopic,
  categoryId,
  setInsightCategories,
  refetchTwinInsightsWithLatest,
}: {
  twinId: string
  categoryId: string
  color: string
  isDeletionDisabled: boolean // we do not allow deletion of the Other topic
  onOpenEditTopicModal: () => void
  onDeleteTopic: () => void
  setInsightCategories: React.Dispatch<React.SetStateAction<TwinInsightCategory[]>>
  refetchTwinInsightsWithLatest: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const setToast = useSetRecoilState(toastAtom)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleEditTopic = () => {
    onOpenEditTopicModal()
    handleClose()
  }

  const handleDeleteTopic = () => {
    onDeleteTopic()
    handleClose()
  }

  const [selectedColor, setSelectedColor] = useState<string>(color)
  const onSelectColor = async (color: string) => {
    if (!activeOrganization) {
      return
    }
    try {
      await twinsUpdateInsightCategory(activeOrganization, twinId, categoryId, {
        color,
      })

      setToast({ title: 'Successfully updated color', severity: 'success' })
      // update local state with new color
      setSelectedColor(color)

      // upodate recoil with the new color
      setInsightCategories((prev) => prev.map((topic) => (topic.id === categoryId ? { ...topic, color } : topic)))

      // refetch insights because the color should be updated
      refetchTwinInsightsWithLatest()

      // should close the menu
      handleClose()
    } catch (err) {
      setToast({ title: 'Color update failed', severity: 'error' })
    }
  }

  return (
    <Stack>
      <IconButton
        onClick={handleClick}
        sx={{
          padding: '4px',
        }}>
        <MoreVertOutlinedIcon
          sx={{
            height: '16px',
            width: '16px',
          }}
        />
      </IconButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={{
          width: '200px',
        }}>
        <MenuItem onClick={handleEditTopic}>
          <ListItemIcon
            sx={{
              marginRight: '-8px',
            }}>
            <EditOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="pSM">Edit topic</Typography>
        </MenuItem>
        {!isDeletionDisabled && (
          <MenuItem onClick={handleDeleteTopic}>
            <ListItemIcon
              sx={{
                marginRight: '-8px',
              }}>
              <DeleteOutlinedIcon
                fontSize="small"
                sx={{
                  color: COLORS.RED,
                }}
              />
            </ListItemIcon>
            <Typography
              variant="pSM"
              sx={{
                color: COLORS.RED,
              }}>
              Delete topic
            </Typography>
          </MenuItem>
        )}
        <TopicLabelPicker selectedColor={selectedColor} onSelectColor={onSelectColor} />
      </Menu>
    </Stack>
  )
}

const TopicItem = ({
  isSelected,
  category,
  setSelectedCategory,
  twinId,
  setInsightCategories,
  refetchTwinInsightsWithLatest,
  disabled,
}: {
  isSelected: boolean
  category: TwinInsightCategory
  setSelectedCategory: React.Dispatch<React.SetStateAction<TwinInsightCategory | null>>
  twinId: string
  setInsightCategories: React.Dispatch<React.SetStateAction<TwinInsightCategory[]>>
  refetchTwinInsightsWithLatest: () => void
  disabled: boolean
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [isHovered, setIsHovered] = useState<boolean>(false)

  // local state for edit modal
  const [isEditTopicModalOpen, setIsEditTopicModalOpen] = useState(false)
  const onOpenEditTopicModal = () => {
    setIsEditTopicModalOpen(true)
  }

  const handleDeleteTopic = async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    try {
      await twinsDeleteInsightCategories(activeOrganization, twinId, [category.id])
      // update local client state
      setInsightCategories((prev) => prev.filter((topic) => topic.id !== category.id))
      refetchTwinInsightsWithLatest()

      setToast({ title: 'Successfully deleted topic', severity: 'success' })
    } catch (e) {
      setToast({ title: 'Failed to delete', severity: 'error' })
    }
  }

  return (
    <Stack
      height="24px"
      direction="row"
      alignItems="center"
      spacing={1}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        if (!disabled) {
          setSelectedCategory(category)
        }
      }}
      sx={{
        cursor: disabled ? 'inherit' : 'pointer',
      }}>
      <Stack
        width="100%"
        borderRadius="4px"
        p="4px"
        direction="row"
        alignItems="center"
        sx={{
          background: disabled
            ? COLORS.PURE_WHITE
            : isHovered || isSelected
            ? COLORS.JELLED_MID_BLUE_10_PERCENT
            : COLORS.PURE_WHITE,
        }}>
        <Stack
          sx={{
            background: category.color,
            width: '12px',
            height: '12px',
            borderRadius: '3px',
            mr: 1,
          }}
        />
        <Typography
          variant="pXS"
          sx={{
            color: disabled ? COLORS.GRAY_3 : isHovered ? COLORS.JELLED_PRIMARY_BLUE : COLORS.GRAY_1,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}>
          {category.name}
        </Typography>
      </Stack>

      <Stack
        sx={{
          opacity: isHovered ? 1 : 0,
        }}>
        <TopicItemMenu
          color={category.color}
          twinId={twinId}
          categoryId={category.id}
          isDeletionDisabled={category.name === 'Other'}
          onOpenEditTopicModal={onOpenEditTopicModal}
          onDeleteTopic={handleDeleteTopic}
          setInsightCategories={setInsightCategories}
          refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
        />
      </Stack>

      {isEditTopicModalOpen && (
        <EditTopicModal
          insightCategory={category}
          twinId={twinId}
          onClose={() => setIsEditTopicModalOpen(false)}
          onTopicEdited={(updatedTopic) => {
            // update local state with new insight
            setInsightCategories((prev) => prev.map((topic) => (topic.id === updatedTopic.id ? updatedTopic : topic)))
          }}
        />
      )}
    </Stack>
  )
}

const SuggestedTopicItem = ({
  onAddSuggestedTopic,
  topic,
  twinId,
  setInsightCategories,
  refetchTwinInsightsWithLatest,
}: {
  onAddSuggestedTopic: (topic: TwinInsightCategory) => void
  topic: TwinInsightCategory
  twinId: string
  setInsightCategories: React.Dispatch<React.SetStateAction<TwinInsightCategory[]>>
  refetchTwinInsightsWithLatest: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  const [isHovered, setIsHovered] = useState<boolean>(false)

  const handleDeleteTopic = async () => {
    if (!activeOrganization || !twinId) {
      return
    }

    try {
      await twinsDeleteInsightCategories(activeOrganization, twinId, [topic.id])
      // update local client state
      setInsightCategories((prev) => prev.filter((t) => t.id !== topic.id))

      // we should be making a refetch latest insights call here whenever a suggested topic is deleted
      refetchTwinInsightsWithLatest()
      setToast({ title: 'Successfully deleted topic', severity: 'success' })
    } catch (e) {
      setToast({ title: 'Failed to delete', severity: 'error' })
    }
  }

  const isDeletionDisabled = topic.name === 'Other'

  return (
    <Stack
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      direction="row"
      alignItems="center"
      width="100%"
      justifyContent="space-between">
      <Chip
        size="small"
        icon={<AddOutlinedIcon />}
        onClick={() => onAddSuggestedTopic(topic)}
        label={topic.name}
        variant="outlined"
        sx={{
          width: 'fit-content',
          maxWidth: '258px',
          fontSize: '12px',
          fontWeight: 500,
          border: `1px solid ${COLORS.GRAY_5}`,
        }}
      />

      <IconButton
        onClick={handleDeleteTopic}
        sx={{
          opacity: isHovered && !isDeletionDisabled ? 1 : 0,
        }}>
        <CloseOutlinedIcon
          sx={{
            height: '12px',
            width: '12px',
          }}
        />
      </IconButton>
    </Stack>
  )
}

const SuggestedTopics = ({
  suggestedTopics,
  setInsightCategories,
  twinId,
  refetchTwinInsightsWithLatest,
}: {
  suggestedTopics: TwinInsightCategory[]
  setInsightCategories: React.Dispatch<React.SetStateAction<TwinInsightCategory[]>>
  twinId: string
  refetchTwinInsightsWithLatest: () => void
}) => {
  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)

  // TODO: get this from the API
  const [isHidingTopics, setIsHidingTopics] = useState<boolean>(true)

  const onAddSuggestedTopic = async (category: TwinInsightCategory) => {
    // TODO: we may want a UX for adding instruction to a suggested topic, will do this later
    const updatedInsightCategory = {
      ...category,
      type: TwinInsightCategoryType.user,
    }

    if (!activeOrganization || !twinId) {
      return
    }

    try {
      const response = await twinsUpdateInsightCategories(activeOrganization, twinId, [updatedInsightCategory])
      // update local state with updated insight
      setInsightCategories((prev) => prev.map((topic) => (topic.id === response.data[0].id ? response.data[0] : topic)))

      refetchTwinInsightsWithLatest()

      // TODO: we also need to remove that suggested topic from the list
      setToast({ title: 'Successfully added suggested topic', severity: 'success' })
    } catch (e) {
      setToast({ title: 'Failed to add suggested topic', severity: 'error' })
    }
  }

  if (suggestedTopics.length === 0) {
    return null
  }

  return (
    <Stack width="100%" mt={3}>
      <Typography
        variant="h7.5"
        sx={{
          color: COLORS.GRAY_3,
        }}>
        Suggested
      </Typography>

      <Stack spacing="4px" pt={1}>
        {suggestedTopics.slice(0, isHidingTopics ? 5 : suggestedTopics.length).map((topic, index) => (
          <SuggestedTopicItem
            key={index}
            onAddSuggestedTopic={onAddSuggestedTopic}
            topic={topic}
            twinId={twinId}
            setInsightCategories={setInsightCategories}
            refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
          />
        ))}
      </Stack>

      {suggestedTopics.length > 5 && isHidingTopics && (
        <Button
          variant="text"
          onClick={() => {
            setIsHidingTopics(false)
          }}
          sx={{
            mt: 1,
            width: 'fit-content',
            alignSelf: 'center',
          }}>
          View more
        </Button>
      )}
    </Stack>
  )
}

const TopicsSidebar = ({
  selectedCategory,
  setSelectedCategory,
  refetchTwinInsightsWithLatest,
  insightCategoryIds,
}: {
  selectedCategory: TwinInsightCategory | null
  setSelectedCategory: React.Dispatch<React.SetStateAction<TwinInsightCategory | null>>
  refetchTwinInsightsWithLatest: () => void
  insightCategoryIds: string[]
}) => {
  const isMobile = useCheckMobileScreen()
  const myTwin = useRecoilValue(myTwinAtom)

  const { insightCategories, setInsightCategories } = useFetchInsightCategories(myTwin?.id ?? '')

  const enabledInsightCategories = insightCategories.filter((i) => insightCategoryIds.includes(i.id))
  const disabledInsightCategories = insightCategories.filter((i) => !insightCategoryIds.includes(i.id))

  const sortedEnabledInsightCategories = enabledInsightCategories.sort((a, b) => {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  })

  const sortedDisabledInsightCategories = disabledInsightCategories.sort((a, b) => {
    if (a.name < b.name) {
      return -1
    }
    if (a.name > b.name) {
      return 1
    }
    return 0
  })

  const [isAddTopicModalOpen, setIsAddTopicModalOpen] = useState(false)
  const onAddTopicClick = () => {
    setIsAddTopicModalOpen(true)
  }

  return (
    <SidebarContainerStack
      width={isMobile ? '100%' : '320px'}
      minWidth={isMobile ? '100%' : '320px'}
      height="fit-content"
      p="12px"
      alignItems="flex-start"
      borderRadius="8px"
      border={`1px solid ${COLORS.GRAY_6}`}
      sx={{
        background: COLORS.PURE_WHITE,
      }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
        <Stack direction="row" alignItems="center" spacing={1}>
          <SummarizeOutlinedIcon />

          <Typography
            variant="h5"
            sx={{
              color: COLORS.GRAY_1,
            }}>
            Topics
          </Typography>
        </Stack>

        <Button
          startIcon={<AddOutlinedIcon />}
          variant="text"
          onClick={onAddTopicClick}
          sx={{
            color: COLORS.GRAY_2,
          }}>
          Add topic
        </Button>
      </Stack>
      <Divider
        sx={{
          width: '100%',
          my: '12px',
          borderColor: COLORS.GRAY_5,
        }}
      />

      <Stack width="100%">
        {insightCategories.length > 0 ? (
          <Stack spacing="4px" width="100%">
            {sortedEnabledInsightCategories
              .filter((i) => i.type !== TwinInsightCategoryType.llm)
              .map((category, index) => (
                <TopicItem
                  key={index}
                  isSelected={selectedCategory?.id === category.id}
                  category={category}
                  disabled={!insightCategoryIds.includes(category.id)}
                  setSelectedCategory={setSelectedCategory}
                  twinId={myTwin?.id ?? ''}
                  setInsightCategories={setInsightCategories}
                  refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
                />
              ))}
            {sortedDisabledInsightCategories
              .filter((i) => i.type !== TwinInsightCategoryType.llm)
              .map((category, index) => (
                <TopicItem
                  key={index}
                  isSelected={selectedCategory?.id === category.id}
                  category={category}
                  disabled={!insightCategoryIds.includes(category.id)}
                  setSelectedCategory={setSelectedCategory}
                  twinId={myTwin?.id ?? ''}
                  setInsightCategories={setInsightCategories}
                  refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
                />
              ))}
          </Stack>
        ) : null}
      </Stack>

      <SuggestedTopics
        twinId={myTwin?.id ?? ''}
        suggestedTopics={insightCategories.filter((i) => i.type === TwinInsightCategoryType.llm)}
        setInsightCategories={setInsightCategories}
        refetchTwinInsightsWithLatest={refetchTwinInsightsWithLatest}
      />

      {isAddTopicModalOpen && (
        <AddTopicModal
          twinId={myTwin?.id ?? ''}
          insightCategories={insightCategories}
          onUpdateInsights={(updatedInsights) => {
            // update local state with new list of insights
            setInsightCategories(updatedInsights)

            // refetch twin insights with latest as we have new topics in our sidebar
            refetchTwinInsightsWithLatest()
          }}
          onClose={() => setIsAddTopicModalOpen(false)}
        />
      )}
    </SidebarContainerStack>
  )
}

export default TopicsSidebar
